// § 2 · Inputs
// "How does data get in?"
//
// One sub-card per active input channel + an "Add input" footer. Hidden
// channels (operator hasn't configured email / form / api_pull yet)
// don't surface as empty cards — they show under the Add menu.

import { useState } from 'react';
import type { Source } from '@datahub/shared';
import { useToast } from '../Toast';
import { SectionShell } from './SectionShell';
import { UploadDialog } from '../dialogs/UploadDialog';
import { SendEventDialog } from '../dialogs/SendEventDialog';
import { HowToSendDialog } from '../hero/SourceHero';
import { AddInputDialog } from '../dialogs/AddInputDialog';
import { FormConfigDialog } from '../dialogs/FormConfigDialog';
import { EmailParsePanel } from '../dialogs/EmailParsePanel';
import { RawUploadPanel } from '../dialogs/RawUploadPanel';

interface Props {
  source: Source;
  onSourceChange: (s: Source) => void;
}

export function InputsRailV2({ source, onSourceChange }: Props): React.ReactElement {
  const toast = useToast();
  const [showHow, setShowHow] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showAddInput, setShowAddInput] = useState(false);
  const [showFormConfig, setShowFormConfig] = useState(false);
  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const [showSharePanel, setShowSharePanel] = useState(false);

  const webhookUrl = source.inputs.find(i => i.kind === 'webhook')?.url
    ?? `${window.location.origin}/h/${source.slug_alias ?? source.source_id}`;
  const fileUploadUrl = source.inputs.find(i => i.kind === 'file_upload')?.url
    ?? `${window.location.origin}/u/${source.source_id}`;
  const formInput = source.form_config;
  const emailAddr = source.inputs.find(i => i.kind === 'email')?.address;
  const apiPull = source.inputs.find(i => i.kind === 'api_pull');

  function copy(s: string, label: string) {
    void navigator.clipboard.writeText(s);
    toast.show(`${label} copied.`, { kind: 'success' });
  }

  async function sendTest() {
    try {
      const r = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: 'test', amount: 42, ts: new Date().toISOString() }),
      });
      toast.show(r.ok ? `Test sent · ${r.status}` : `Test failed · ${r.status}`, { kind: r.ok ? 'success' : 'error' });
    } catch (e) {
      toast.show(`Test failed · ${(e as Error).message}`, { kind: 'error' });
    }
  }

  return (
    <SectionShell
      id="inputs"
      title="Inputs"
      subtitle="How does data get into this source?"
      headerExtra={
        <button className="btn sm ghost" onClick={() => setShowAddInput(true)} title="Configure another input channel">+ add input</button>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        {/* Webhook — always present */}
        <InputCard
          label="Webhook"
          help="POST JSON to this URL. Optional HMAC verification (configure in Settings › Security)."
        >
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{ flex: 1, minWidth: 240, padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 11.5, background: 'var(--bg-sunken)', borderRadius: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ink-2)' }}>{webhookUrl}</code>
            <button className="btn sm" onClick={() => copy(webhookUrl, 'Webhook URL')}>copy</button>
            <button className="btn sm" onClick={() => void sendTest()}>send test</button>
            <button className="btn sm ghost" onClick={() => setShowHow(true)}>snippets</button>
          </div>
        </InputCard>

        {/* File upload — always present */}
        <InputCard
          label="File upload"
          help="POST CSV / JSON / NDJSON / XLSX / XML. Bulk mode for >1k rows. Max 25MB."
        >
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{ flex: 1, minWidth: 240, padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 11.5, background: 'var(--bg-sunken)', borderRadius: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ink-2)' }}>{fileUploadUrl}</code>
            <button className="btn sm" onClick={() => copy(fileUploadUrl, 'Upload URL')}>copy</button>
            <button className="btn sm" onClick={() => setShowUpload(true)}>upload now</button>
          </div>
        </InputCard>

        {/* Form (only if configured) */}
        {formInput && (
          <InputCard
            label="Public form"
            help="No-code submission page; partners fill in fields and submit. Subject to per-IP rate limit."
          >
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                {formInput.enabled ? 'Enabled' : 'Disabled'} · {formInput.fields?.length ?? 0} fields
              </span>
              <button className="btn sm" onClick={() => setShowFormConfig(true)} style={{ marginLeft: 'auto' }}>configure</button>
            </div>
          </InputCard>
        )}
        {!formInput && (
          <InputCard
            label="Public form"
            help="Not configured. Operators add a no-code submission page when partners can't POST JSON."
          >
            <button className="btn sm ghost" onClick={() => setShowFormConfig(true)}>configure form</button>
          </InputCard>
        )}

        {/* Email (only if configured) */}
        {emailAddr && (
          <InputCard
            label="Email"
            help="Inbound parse address. Attachments forward to /upload automatically."
          >
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ flex: 1, minWidth: 240, padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 11.5, background: 'var(--bg-sunken)', borderRadius: 4, color: 'var(--ink-2)' }}>{emailAddr}</code>
              <button className="btn sm" onClick={() => copy(emailAddr, 'Inbound address')}>copy</button>
            </div>
          </InputCard>
        )}

        {/* API pull (only if configured) */}
        {apiPull && (
          <InputCard
            label="API pull"
            help="Scheduled cron + on-demand fetch from external URL. SSRF-blocked. 1k row cap per run."
          >
            <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
              Status: <strong>{apiPull.status}</strong> · scheduled pulls configured under Processing › Schedules
            </div>
          </InputCard>
        )}

        {/* Manual paste — always available */}
        <InputCard
          label="Manual paste"
          help="One-off custom payload — useful for debugging trigger behaviour or bootstrapping a row."
        >
          <button className="btn sm" onClick={() => setShowSend(true)}>send custom event</button>
        </InputCard>
      </div>

      {/* Modals — reused as-is from the legacy page (independent of v1/v2 chrome).
          UploadDialog: takes sourceId (slug routing on /upload/:slug). Passing
          `source` was a refactor leftover that resolved to undefined at runtime
          → "/upload/undefined" → source_not_found on every v2 upload attempt.
          SendEventDialog: takes webhookUrl, not source. */}
      {showHow && <HowToSendDialog webhookUrl={webhookUrl} onClose={() => setShowHow(false)} />}
      {showUpload && <UploadDialog sourceId={source.source_id} onClose={() => setShowUpload(false)} />}
      {showSend && <SendEventDialog webhookUrl={webhookUrl} onClose={() => setShowSend(false)} />}
      {showAddInput && (
        <AddInputDialog
          source={source}
          onClose={() => setShowAddInput(false)}
          onOpenEmail={() => setShowEmailPanel(true)}
          onOpenForm={() => setShowFormConfig(true)}
          onOpenUpload={() => setShowUpload(true)}
          onOpenSharePanel={() => setShowSharePanel(true)}
          onOpenSendCustom={() => setShowSend(true)}
          onOpenSchedules={() => document.getElementById('v2-processing')?.scrollIntoView({ behavior: 'smooth' })}
        />
      )}
      {showFormConfig && (
        <FormConfigDialog
          source={source}
          onClose={() => setShowFormConfig(false)}
          onSaved={(cfg) => { setShowFormConfig(false); onSourceChange({ ...source, form_config: cfg ?? undefined }); }}
        />
      )}
      {showEmailPanel && (
        <EmailParsePanel source={source} onClose={() => setShowEmailPanel(false)} />
      )}
      {showSharePanel && (
        <RawUploadPanel source={source} onClose={() => setShowSharePanel(false)} />
      )}
    </SectionShell>
  );
}

function InputCard({ label, help, children }: { label: string; help: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{
      padding: 12,
      background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)' }}>{label}</span>
        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{help}</span>
      </div>
      {children}
    </div>
  );
}
